import type { UsersItemDto } from '@/castapi';
import { AppLogger } from '@/logger';
import type { ISearchParams } from '@/castapi/helpers';
import { getErrorMessage, getUsersApi } from '@/castapi/helpers';
import type { IActionParams } from '@/store/modules/index';
import type { ITableParamsState } from '@/store/modules/common/tableParamsState';
import TableParamsState from '@/store/modules/common/tableParamsState';

const logger = new AppLogger('adminPendingUsers state');
const tableParams = new TableParamsState();

interface IAdminPendingUsers extends ITableParamsState {
  users: UsersItemDto[];
  usersLoading: boolean;
  usersLoadError?: string;
  userChanging?: number;
  deleteUserError?: string;
  updateUserError?: string;
  invitationSending: boolean;
  invitationError?: string;
}

const initialState = (): IAdminPendingUsers => ({
  ...tableParams.state(),
  users: [],
  usersLoading: false,
  usersLoadError: undefined,
  userChanging: undefined,
  deleteUserError: undefined,
  updateUserError: undefined,
  invitationSending: false,
  invitationError: undefined,
});

type ActionParams = IActionParams<IAdminPendingUsers>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    ...tableParams.mutations,

    USERS_LOADING(state: IAdminPendingUsers): void {
      state.usersLoading = true;
      state.usersLoadError = undefined;
    },
    USERS_LOADED(state: IAdminPendingUsers, users: UsersItemDto[]): void {
      state.users = users;
      state.usersLoading = false;
    },
    USERS_LOAD_ERROR(state: IAdminPendingUsers, payload: Error): void {
      state.usersLoading = false;
      state.usersLoadError = getErrorMessage(payload);
    },
    USER_DELETED(state: IAdminPendingUsers): void {
      state.userChanging = undefined;
    },
    DELETE_USER_ERROR(state: IAdminPendingUsers, payload: Error): void {
      state.deleteUserError = getErrorMessage(payload);
      state.userChanging = undefined;
    },
    INVITATION_SENDING(state: IAdminPendingUsers): void {
      state.invitationSending = true;
      state.invitationError = undefined;
    },
    INVITATION_ERROR(state: IAdminPendingUsers, error: Error): void {
      state.invitationSending = false;
      state.invitationError = getErrorMessage(error);
    },
    INVITATION_SENT(state: IAdminPendingUsers): void {
      state.invitationSending = false;
    },
    RESET_STATE(state: IAdminPendingUsers): void {
      Object.assign(state, initialState());
    },
  },
  actions: {
    ...tableParams.actions,

    async loadUsers({ commit, rootGetters }: ActionParams, searchParams: ISearchParams): Promise<void> {
      const { limit, offset, searchText, sortBy, sortDesc } = searchParams || {};
      commit('USERS_LOADING');
      try {
        const response = await getUsersApi(rootGetters['login/accessToken']).usersControllerGetAllPendingUsers(
          limit,
          offset,
          searchText,
          sortBy,
          sortDesc,
        );
        commit('USERS_LOADED', response.data);
      } catch (error) {
        commit('USERS_LOAD_ERROR', error);
        logger.captureStoreError('loadUsers', error, { limit, offset, searchText, sortBy, sortDesc });
      }
    },
    async sendInvitation({ commit, rootGetters }: ActionParams, userId: number): Promise<void> {
      commit('INVITATION_SENDING');
      try {
        await getUsersApi(rootGetters['login/accessToken']).usersControllerSendAccountActivationEmail(userId);
        commit('INVITATION_SENT');
      } catch (error) {
        commit('INVITATION_ERROR', error);
      }
    },
    async sendMultipleInvitations({ commit, rootGetters }: ActionParams, userIds: number[]): Promise<void> {
      commit('INVITATION_SENDING');
      try {
        await getUsersApi(rootGetters['login/accessToken']).usersControllerSendAccountActivationEmails({ userIds });
        commit('INVITATION_SENT');
      } catch (error) {
        commit('INVITATION_ERROR', error);
      }
    },
    async removeUser({ commit, rootGetters }: ActionParams, userId: number): Promise<void> {
      // unused
      commit('USER_CHANGING', userId);
      try {
        await getUsersApi(rootGetters['login/accessToken']).usersControllerDeleteUser({ userId });
        commit('USER_DELETED');
      } catch (error) {
        commit('DELETE_USER_ERROR', error);
        logger.captureStoreError('removeUser', error, { userId });
      }
    },
    resetState({ commit }: ActionParams): void {
      // unused
      commit('RESET_STATE');
    },
  },
  getters: {
    ...tableParams.getters,

    users: (state: IAdminPendingUsers): UsersItemDto[] => state.users,
    invitationError: (state: IAdminPendingUsers): undefined | string => state.invitationError,
    // invitationSending: (state:IAdminPendingUsers) => state.invitationSending,
    usersLoading: (state: IAdminPendingUsers): boolean => state.usersLoading,
    usersLoadError: (state: IAdminPendingUsers): undefined | string => state.usersLoadError,
  },
};
